import { useState, useEffect, useCallback } from "react";
import { User, Mail, Building, Shield, Key, MapPin, Phone, Save, Lock, Loader2, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { useAgent } from "@/contexts/AgentContext";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const availableStates = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
];

const Settings = () => {
  const { state, actions } = useAgent();
  const { user } = useAuth();
  const { toast } = useToast();
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [emailChangeMode, setEmailChangeMode] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailChanging, setEmailChanging] = useState(false);

  const [profile, setProfile] = useState({
    firstName: state.profile.firstName,
    lastName: state.profile.lastName,
    email: state.profile.email,
    phone: state.profile.phone,
    company: state.profile.company,
  });

  const [license, setLicense] = useState({
    number: state.profile.licenseNumber,
    states: [...state.profile.licensedStates],
  });

  const [security, setSecurity] = useState({
    mfaEnabled: state.security.mfaEnabled,
    emailNotifications: state.security.emailNotifications,
    smsNotifications: state.security.smsNotifications,
  });

  useEffect(() => {
    setProfile({
      firstName: state.profile.firstName,
      lastName: state.profile.lastName,
      email: state.profile.email,
      phone: state.profile.phone,
      company: state.profile.company,
    });
    setLicense({
      number: state.profile.licenseNumber,
      states: [...state.profile.licensedStates],
    });
    setSecurity({
      mfaEnabled: state.security.mfaEnabled,
      emailNotifications: state.security.emailNotifications,
      smsNotifications: state.security.smsNotifications,
    });
  }, [state.profile, state.security]);

  const handleProfileChange = (field: string, value: string) => {
    setProfile((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const toggleState = (stateCode: string) => {
    setLicense((prev) => ({
      ...prev,
      states: prev.states.includes(stateCode)
        ? prev.states.filter((s) => s !== stateCode)
        : [...prev.states, stateCode],
    }));
    setHasChanges(true);
  };

  const handleSecurityChange = (field: string, value: boolean) => {
    setSecurity((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSaveAll = async () => {
    // Validate: at least one state required
    if (license.states.length === 0) {
      toast({
        title: "Validation error",
        description: "You must be licensed in at least one state.",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    const fullName = `${profile.firstName} ${profile.lastName}`.trim();
    const agentId = state.profile.id;
    console.log("[Settings] Saving for agent:", agentId, "name:", fullName);

    // Persist profile + licensed states to Supabase agents table
    // NOTE: email is NOT updated here — changing the login email requires
    // supabase.auth.updateUser() which sends a confirmation email.
    // The agents.email column stays in sync with the auth email.
    const { data: updateResult, error } = await supabase
      .from("agents")
      .update({
        name: fullName,
        company: profile.company,
        phone: profile.phone || null,
        license_number: license.number || null,
        licensed_states: license.states,
        sms_notifications_enabled: security.smsNotifications,
        email_notifications_enabled: security.emailNotifications,
      })
      .eq("id", agentId)
      .select("id");

    console.log("[Settings] Update result:", updateResult, "error:", error);

    if (error) {
      console.error("[Settings] Failed to save:", error);
      setSaving(false);
      toast({
        title: "Save failed",
        description: `Could not save settings: ${error.message}`,
        variant: "destructive",
      });
      return;
    }

    if (!updateResult || updateResult.length === 0) {
      console.error("[Settings] Update matched 0 rows — agent_id may not match auth.uid()", agentId);
      setSaving(false);
      toast({
        title: "Save failed",
        description: "No agent record found for your account. This may be a configuration issue — contact support.",
        variant: "destructive",
      });
      return;
    }

    // Update context to reflect saved state
    actions.updateProfile({
      firstName: profile.firstName,
      lastName: profile.lastName,
      name: fullName,
      email: profile.email,
      phone: profile.phone,
      company: profile.company,
      licenseNumber: license.number,
    });
    actions.updateLicensedStates(license.states);
    actions.updateSecuritySettings(security);

    setSaving(false);
    setHasChanges(false);
    toast({
      title: "Settings saved",
      description: "Your profile and license information have been updated.",
    });
  };

  const handleChangeEmail = useCallback(async () => {
    const trimmed = newEmail.trim().toLowerCase();
    if (!trimmed || trimmed === user?.email) {
      toast({
        title: "No change",
        description: "Please enter a different email address.",
        variant: "destructive",
      });
      return;
    }

    setEmailChanging(true);
    const { error } = await supabase.auth.updateUser({ email: trimmed });
    setEmailChanging(false);

    if (error) {
      toast({
        title: "Email change failed",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Confirmation email sent",
      description: `We sent a confirmation link to ${trimmed}. Click the link to complete the change.`,
    });
    setEmailChangeMode(false);
    setNewEmail("");
  }, [newEmail, user?.email, toast]);

  const handleChangePassword = async () => {
    if (!user?.email) return;
    const { error } = await supabase.auth.resetPasswordForEmail(user.email);
    if (error) {
      toast({
        title: "Error",
        description: "Could not send password reset email. Please try again.",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Password reset email sent",
        description: "Check your inbox for a link to reset your password.",
      });
    }
  };

  const handleDeactivate = async () => {
    const confirmed = window.confirm(
      "Are you sure you want to deactivate your account? All lead delivery will be paused and you will be signed out."
    );
    if (!confirmed) return;

    const { error } = await supabase
      .from("agents")
      .update({ is_active: false })
      .eq("id", state.profile.id);

    if (error) {
      toast({
        title: "Error",
        description: "Could not deactivate account. Please try again.",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Account deactivated",
        description: "Your account has been deactivated. You will be signed out.",
      });
      // Sign out after a short delay so the user sees the toast
      setTimeout(async () => {
        await supabase.auth.signOut();
      }, 1500);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold text-foreground">Settings</h1>
            <p className="text-sm text-muted-foreground">Manage your account and preferences</p>
          </div>
          <Button className="gap-2 w-full sm:w-auto" disabled={!hasChanges || saving} onClick={handleSaveAll}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? "Saving..." : "Save All Changes"}
          </Button>
        </div>

        {/* Profile Section */}
        <div className="bg-card rounded-2xl border border-border overflow-hidden">
          <div className="px-6 py-4 bg-primary/5 border-b border-border flex items-center gap-3">
            <User className="w-5 h-5 text-primary" />
            <div>
              <h2 className="font-display text-lg font-semibold text-foreground">Profile Information</h2>
              <p className="text-sm text-muted-foreground">Your personal and business details</p>
            </div>
          </div>
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-sm font-medium text-foreground block mb-2">First Name</label>
                <Input
                  value={profile.firstName}
                  onChange={(e) => handleProfileChange("firstName", e.target.value)}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground block mb-2">Last Name</label>
                <Input
                  value={profile.lastName}
                  onChange={(e) => handleProfileChange("lastName", e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-sm font-medium text-foreground block mb-2">
                  <Mail className="w-4 h-4 inline mr-2" />
                  Email Address
                </label>
                {emailChangeMode ? (
                  <div className="space-y-2">
                    <Input
                      type="email"
                      placeholder="Enter new email address"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      autoFocus
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        disabled={emailChanging || !newEmail.trim()}
                        onClick={handleChangeEmail}
                      >
                        {emailChanging ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                        Send Confirmation
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => { setEmailChangeMode(false); setNewEmail(""); }}
                      >
                        Cancel
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      A confirmation link will be sent to the new address. Your email won't change until you click it.
                    </p>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Input
                      type="email"
                      value={user?.email || profile.email}
                      disabled
                      className="opacity-70 flex-1"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      className="shrink-0"
                      onClick={() => setEmailChangeMode(true)}
                    >
                      <Pencil className="w-3 h-3 mr-1" />
                      Change
                    </Button>
                  </div>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-foreground block mb-2">
                  <Phone className="w-4 h-4 inline mr-2" />
                  Phone Number
                </label>
                <Input
                  type="tel"
                  value={profile.phone}
                  onChange={(e) => handleProfileChange("phone", e.target.value)}
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-foreground block mb-2">
                <Building className="w-4 h-4 inline mr-2" />
                Company / Agency Name
              </label>
              <Input
                value={profile.company}
                onChange={(e) => handleProfileChange("company", e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* License Section */}
        <div className="bg-card rounded-2xl border border-border overflow-hidden">
          <div className="px-6 py-4 bg-primary/5 border-b border-border flex items-center gap-3">
            <Key className="w-5 h-5 text-primary" />
            <div>
              <h2 className="font-display text-lg font-semibold text-foreground">License Information</h2>
              <p className="text-sm text-muted-foreground">Your professional licensing details</p>
            </div>
          </div>
          <div className="p-6 space-y-6">
            <div>
              <label className="text-sm font-medium text-foreground block mb-2">License Number</label>
              <Input
                value={license.number}
                onChange={(e) => {
                  setLicense({ ...license, number: e.target.value });
                  setHasChanges(true);
                }}
              />
              <p className="text-xs text-muted-foreground mt-1">Your primary insurance or mortgage license number</p>
            </div>

            <div>
              <label className="text-sm font-medium text-foreground block mb-3">
                <MapPin className="w-4 h-4 inline mr-2" />
                States Licensed ({license.states.length} selected)
              </label>
              <div className="flex flex-wrap gap-2 p-4 bg-muted/30 rounded-xl border border-border max-h-48 overflow-y-auto">
                {availableStates.map((stateCode) => (
                  <button
                    key={stateCode}
                    onClick={() => toggleState(stateCode)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      license.states.includes(stateCode)
                        ? "bg-primary text-primary-foreground"
                        : "bg-card border border-border text-muted-foreground hover:text-foreground hover:border-primary/50"
                    }`}
                  >
                    {stateCode}
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-2">You'll only receive leads from states where you're licensed</p>
            </div>
          </div>
        </div>

        {/* Security Section */}
        <div className="bg-card rounded-2xl border border-border overflow-hidden">
          <div className="px-6 py-4 bg-primary/5 border-b border-border flex items-center gap-3">
            <Shield className="w-5 h-5 text-primary" />
            <div>
              <h2 className="font-display text-lg font-semibold text-foreground">Security</h2>
              <p className="text-sm text-muted-foreground">Password and account security settings</p>
            </div>
          </div>
          <div className="p-6 space-y-6">
            {/* Change Password */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-muted/30 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Lock className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium text-foreground">Password</p>
                  <p className="text-sm text-muted-foreground">Send a password reset link to your email</p>
                </div>
              </div>
              <Button variant="outline" className="w-full sm:w-auto" onClick={handleChangePassword}>Change Password</Button>
            </div>

            {/* MFA Toggle */}
            <div className="flex items-center justify-between p-4 bg-muted/30 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium text-foreground">Two-Factor Authentication</p>
                  <p className="text-sm text-muted-foreground">Coming soon</p>
                </div>
              </div>
              <Switch
                checked={security.mfaEnabled}
                onCheckedChange={(checked) => handleSecurityChange("mfaEnabled", checked)}
                disabled
              />
            </div>

            {/* Notification Settings */}
            <div className="pt-4 border-t border-border">
              <h3 className="font-medium text-foreground mb-4">Notification Preferences</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-foreground">Email Notifications</p>
                    <p className="text-xs text-muted-foreground">Receive lead alerts via email</p>
                  </div>
                  <Switch
                    checked={security.emailNotifications}
                    onCheckedChange={(checked) => handleSecurityChange("emailNotifications", checked)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-foreground">SMS Notifications</p>
                    <p className="text-xs text-muted-foreground">Receive lead alerts via text message</p>
                  </div>
                  <Switch
                    checked={security.smsNotifications}
                    onCheckedChange={(checked) => handleSecurityChange("smsNotifications", checked)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="bg-card rounded-2xl border border-destructive/30 overflow-hidden">
          <div className="px-6 py-4 bg-destructive/5 border-b border-destructive/30">
            <h2 className="font-display text-lg font-semibold text-destructive">Danger Zone</h2>
            <p className="text-sm text-muted-foreground">Irreversible account actions</p>
          </div>
          <div className="p-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <p className="font-medium text-foreground">Deactivate Account</p>
                <p className="text-sm text-muted-foreground">Temporarily disable your account and pause all lead delivery</p>
              </div>
              <Button
                variant="outline"
                className="border-destructive/50 text-destructive hover:bg-destructive/10 w-full sm:w-auto"
                onClick={handleDeactivate}
              >
                Deactivate
              </Button>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Settings;
